import { Type, Static, FormatRegistry } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  StringField,
  TextField,
  IntegerField,
  TimestampField,
  RelationOne,
  RelationField,
  UuidField,
} from '@ayocore/platform/common/helpers/schema-types';

import userSchemaDefinition from '@ayocore/platform/identity/schemas/user.schema';
import partnerStatusSchemaDefinition from './partner-status.schema';
import salesAreaSchemaDefinition from './sales-area.schema';
import leadSourceSchemaDefinition from './lead-source.schema';

import { name as moduleName } from '../module.json';
// FIXME: подключал общий registry глобально - возможно тут нет необходимости это делать - надо протестировать
// Setup email format validation for TypeBox schemas
FormatRegistry.Set('email', value =>
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(
    value
  )
);

/**
 * Partner Schema - Companies/organizations we work with
 *
 * Based on client.json data structure with CRM enhancements.
 * Represents business partners, clients, and prospects.
 */

// Complete schema (with system fields)
export const PartnerSchema = Type.Object({
  ...SystemFields, // Platform-provided system fields (id, created_at, updated_at)

  name: StringField({
    title: 'Название партнера',
    minLength: 1,
    maxLength: 255,
    description: 'Название компании-партнера',
    error: 'Введите название компании',
  }),

  full_name: StringField({
    title: 'Полное наименование',
    nullable: true,
    maxLength: 500,
    description: 'Полное юридическое наименование компании',
  }),

  address: TextField({
    title: 'Адрес',
    nullable: true,
    description: 'Адрес компании',
  }),

  // Business details
  director: StringField({
    title: 'Директор',
    nullable: true,
    maxLength: 255,
    description: 'Имя директора/генерального директора компании',
  }),

  inn: StringField({
    title: 'ИНН',
    nullable: true,
    maxLength: 20,
    description: 'Идентификационный номер налогоплательщика',
  }),

  ogrn: StringField({
    title: 'ОГРН',
    nullable: true,
    maxLength: 20,
    description: 'Основной государственный регистрационный номер',
  }),

  kpp: StringField({
    title: 'КПП',
    nullable: true,
    maxLength: 20,
    description: 'Код причины постановки на учёт',
  }),

  okpo: StringField({
    title: 'ОКПО',
    nullable: true,
    maxLength: 20,
    description: 'Код по общероссийскому классификатору предприятий и организаций',
  }),

  // Customer status relation (new normalized approach)
  customer_status: RelationOne(partnerStatusSchemaDefinition), // For TypeScript type
  customer_status_id: RelationField(partnerStatusSchemaDefinition, 'ID статуса клиента', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'ID статуса взаимодействия с клиентом из справочника crm_partner_status',
    relationName: 'customer_status',
  }),

  // Sales area relation (one-to-one)
  sales_area: RelationOne(salesAreaSchemaDefinition), // For TypeScript type
  sales_area_id: RelationField(salesAreaSchemaDefinition, 'ID территории продаж', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'ID территории продаж из справочника crm_sales_areas',
    relationName: 'sales_area',
  }),

  notes: TextField({
    title: 'Примечания',
    nullable: true,
    description: 'Внутренние заметки о партнере',
  }),

  // Contact tracking
  first_contact_date: TimestampField({
    title: 'Дата первого контакта',
    nullable: true,
    description: 'Дата первого контакта с партнером',
  }),

  last_contact_date: TimestampField({
    title: 'Дата последнего контакта',
    nullable: true,
    description: 'Дата последнего контакта с партнером',
  }),

  next_contact_date: TimestampField({
    title: 'Дата следующего контакта',
    nullable: true,
    description: 'Запланированная дата следующего контакта',
  }),

  // Manager assignment (link to identity users)
  manager: RelationOne(userSchemaDefinition), // For TypeScript type
  manager_id: RelationField(userSchemaDefinition, 'ID менеджера', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Ответственный менеджер',
    relationName: 'manager',
  }),
  // ✨ Tree structure - will be added after schema definition
  archived_at: TimestampField({
    title: 'Дата архивации',
    nullable: true,
    description: 'Дата помещения партнёра в архив. NOT NULL = архивный.',
  }),

  archived_by: RelationOne(userSchemaDefinition),
  archived_by_id: RelationField(userSchemaDefinition, 'ID автора архивации', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Пользователь, поместивший партнёра в архив',
    relationName: 'archived_by',
  }),

  linked_id: IntegerField({
    title: 'Связанный (дублирующий) партнер',
    nullable: true,
    description: 'ID мастер-партнёра, в который был объединён текущий. NOT NULL = дубль.',
  }),

  linked_at: TimestampField({
    title: 'Дата объединения',
    nullable: true,
    description: 'Когда партнёр был объединён с мастером (linked_id).',
  }),

  avatar_thumb_file_uuid: UuidField({
    title: 'UUID аватара (миниатюра)',
    defaultRaw: 'NULL',
    nullable: true,
    description: 'UUID файла в filemanager_uploaded_files — миниатюра 64×64',
  }),

  avatar_medium_file_uuid: UuidField({
    title: 'UUID аватара (средний)',
    defaultRaw: 'NULL',
    nullable: true,
    description: 'UUID файла в filemanager_uploaded_files — аватар 256×256',
  }),

  // Источник партнёра — тот же справочник, что у лидов (crm_lead_sources).
  // Обязателен для партнёров с ролью «Клиент» — проверка в partner_rules при создании.
  source: RelationOne(leadSourceSchemaDefinition),
  source_id: RelationField(leadSourceSchemaDefinition, 'Источник', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Откуда пришёл партнёр. Справочник общий с источниками лидов.',
    relationName: 'source',
  }),

  /**
   * Из какого лида заведён партнёр — пометка происхождения, а НЕ источник.
   * Заполняется только при создании партнёра из карточки лида.
   *
   * Сознательно без внешнего ключа: `crm_leads` уже ссылается на `crm_partners`,
   * и встречный FK ловится топологической сортировкой схем как цикл —
   * determineTableOrder() бросает «Circular dependency detected» на старте.
   * Лиды удаляются мягко, так что висячая ссылка маловероятна, но потребитель
   * обязан переживать отсутствие лида.
   */
  created_from_lead_id: IntegerField({
    title: 'Создан из лида',
    nullable: true,
    description: 'ID лида, из карточки которого заведён партнёр (без FK — см. комментарий в схеме).',
  }),

  search_text: StringField({
    title: 'Поисковый текст',
    nullable: true,
    maxLength: 1000,
    description:
      'Нормализованный текст для поиска: name + full_name, lowercase, без пробелов и тире, й→и, ё→е, э→е. Заполняется автоматически.',
  }),
});

// TypeScript types derived from schemas
export type Partner = Static<typeof PartnerSchema>;

// Schema definition with automatic table prefixing
export default defineSchema({
  moduleName,
  tableName: 'partners', // System will prefix with 'crm_'
  softDelete: true,
  schema: PartnerSchema,
  additionalIndexes: [
    'name',
    'customer_status_id',
    'sales_area_id',
    'manager_id',
    'linked_id',
    'search_text',
    'source_id',
  ],
  foreignKeys: [
    {
      column: 'linked_id',
      references: { table: 'crm_partners', column: 'id' },
      onDelete: 'SET NULL',
    },
    {
      column: 'avatar_thumb_file_uuid',
      references: { table: 'filemanager_uploaded_files', column: 'uuid' },
      onDelete: 'SET NULL',
    },
    {
      column: 'avatar_medium_file_uuid',
      references: { table: 'filemanager_uploaded_files', column: 'uuid' },
      onDelete: 'SET NULL',
    },
    // FK для customer_status_id и sales_area_id создаются автоматически через RelationField
  ],
  dependencies: [
    'identity_users',
    'crm_partner_status',
    'crm_sales_areas',
    'crm_lead_sources',
    'filemanager_uploaded_files',
  ],
});
